import { fetchSvgText } from "@gettactic/map-builder/src/utils/svgAssetCache";

/** Largest canvas side most browsers accept (iOS Safari is the floor). */
const MAX_CANVAS_DIMENSION = 16384;

export interface ExportPngOptions {
  width: number;
  height: number;
  pixelRatio?: number;
  filename?: string;
  background?: string;
}

const toDataUri = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** Images the browser will not paint from a detached SVG (blob and http
 *  hrefs) are inlined as data URIs; unreachable ones are dropped. */
const inlineImages = async (root: SVGElement): Promise<void> => {
  const images = Array.from(root.querySelectorAll("image"));
  await Promise.all(
    images.map(async (image) => {
      const href =
        image.getAttribute("href") ?? image.getAttribute("xlink:href");
      if (!href || href.startsWith("data:")) return;
      try {
        if (href.endsWith(".svg")) {
          const text = await fetchSvgText(href);
          image.setAttribute(
            "href",
            `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`
          );
          return;
        }
        const response = await fetch(href);
        image.setAttribute("href", await toDataUri(await response.blob()));
      } catch {
        image.remove();
      }
    })
  );
};

/**
 * Rasterises the document layer of the editor's SVG to a PNG download:
 * the editing area at `pixelRatio`, editor chrome (grid, overlay, draft)
 * left out. Replaces the Konva stage export for the SVG editor.
 */
export const exportSvgAsPng = async (
  svg: SVGSVGElement,
  options: ExportPngOptions
): Promise<boolean> => {
  const {
    width,
    height,
    pixelRatio = 3,
    filename = `map-${Date.now()}.png`,
    background = "#FFFFFF"
  } = options;
  const document = svg.querySelector<SVGGElement>('[data-layer="document"]');
  if (!document) return false;

  const ratio = Math.min(
    pixelRatio,
    MAX_CANVAS_DIMENSION / width,
    MAX_CANVAS_DIMENSION / height
  );
  const clone = window.document.createElementNS(
    "http://www.w3.org/2000/svg",
    "svg"
  );
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("viewBox", `0 0 ${width} ${height}`);
  clone.setAttribute("width", String(width * ratio));
  clone.setAttribute("height", String(height * ratio));
  const backdrop = window.document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect"
  );
  backdrop.setAttribute("width", String(width));
  backdrop.setAttribute("height", String(height));
  backdrop.setAttribute("fill", background);
  clone.appendChild(backdrop);
  clone.appendChild(document.cloneNode(true));
  await inlineImages(clone);

  const markup = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(
    new Blob([markup], { type: "image/svg+xml" })
  );
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("SVG rasterisation failed"));
      element.src = url;
    });
    const canvas = window.document.createElement("canvas");
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const context = canvas.getContext("2d");
    if (!context) return false;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const png = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    if (!png) return false;
    const link = window.document.createElement("a");
    link.href = URL.createObjectURL(png);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
};
